import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Button, Checkbox, Column, Label, List, Row, TextInput } from '@pella/sash-framework';
import { isValidEmail, isValidPhoneNumber, isValidPostal } from '../../../utilities/validations';
import { SUBMIT_CCPA_FORM } from '../../../../enums/graphql';
import AddressValidation from '../../address-validation/v3';

import './index.css';

const CCPAForm = () => {
	const [values, setValues] = useState({ firstName: '', lastName: '', phoneNumber: '', email: '', associatedPerson: false, privacyRequest: '', deleteAcknowledgement: false });
	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState('idle');
	const [address, setAddress] = useState({ line_1: '', city: '', state: '', postcode: '', postalCode: '' });
	const [addressComplete, setAddressComplete] = useState(false);
	const [addressModified, setAddressModified] = useState(false);
	const [forceAddressValidation, setForceAddressValidation] = useState(false);
	const [previousAddress, setPreviousAddress] = useState(false);
	const [submitForm] = useMutation(SUBMIT_CCPA_FORM, { onCompleted: () => onFormSuccess() });

	const errorMessages = {
		firstName: 'First Name is required',
		lastName: 'Last Name is required',
		address: 'Valid Address is required',
		phoneNumber: 'Valid Phone Number is required',
		email: 'Valid Email Address is required',
		associatedPerson: 'Associated Person is required',
		privacyRequest: 'Please select an option',
		deleteAcknowledgement: 'Delete Acknowledgement is required',
		postalCode: 'Valid Postal Code is required',
	};

	const onInputChange = (event) => {
		const { checked, name, value, type } = event.target;

		let savedValue = value;
		if (type === 'checkbox') savedValue = checked;

		setValues({ ...values, [name]: savedValue });

		if (name !== 'email') {
			validateField(name, savedValue);
		}
	};

	const onInputBlur = (event) => {
		const { name, value } = event.target;
		if (name === 'email') {
			validateField(name, value);
		}
	};

	const validateField = (name, value) => {
		let error = '';

		if (name === 'email') {
			if (!value || (value && !isValidEmail(value))) {
				error = errorMessages.email;
			}
		} else if (name === 'phoneNumber') {
			if (!value || (value && !isValidPhoneNumber(value))) {
				error = errorMessages.phoneNumber;
			}
		} else if (name === 'firstName' && !value) {
			error = errorMessages.firstName;
		} else if (name === 'lastName' && !value) {
			error = errorMessages.lastName;
		} else if (name === 'associatedPerson' && !value) {
			error = errorMessages.associatedPerson;
		} else if (name === 'privacyRequest' && !value) {
			error = errorMessages.privacyRequest;
		} else if (name === 'deleteAcknowledgement' && !value) {
			error = errorMessages.deleteAcknowledgement;
		} else if (name === 'postalCode' && (!value || !isValidPostal(value))) {
			error = errorMessages.postalCode;
		}

		setErrors({ ...errors, [name]: error });
	};

	const validateForm = () => {
		const errors = {};

		if (!values.firstName) errors.firstName = errorMessages.firstName;
		if (!values.lastName) errors.lastName = errorMessages.lastName;
		if (!address.address1 || !address.city || !address.state || !address.postalCode) errors.address = errorMessages.address;
		if (!isValidPostal(address.postalCode)) errors.postalCode = errorMessages.postalCode;
		if (!values.phoneNumber || (values.phoneNumber && !isValidPhoneNumber(values.phoneNumber))) errors.phoneNumber = errorMessages.phoneNumber;
		if (!values.email || (values.email && !isValidEmail(values.email))) errors.email = errorMessages.email;
		if (!values.associatedPerson) errors.associatedPerson = errorMessages.associatedPerson;
		if (!values.privacyRequest) errors.privacyRequest = errorMessages.privacyRequest;
		if (values.privacyRequest === 'Privacy Deletion Request' && !values.deleteAcknowledgement) errors.deleteAcknowledgement = errorMessages.deleteAcknowledgement;

		setForceAddressValidation(true);
		setErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const onFormSubmit = async (event) => {
		event.preventDefault();
		setStatus('processing');

		if (!validateForm()) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			setStatus('error');
			return;
		}

		const input = {
			data: {
				address: address.address1,
				associatedPerson: values.associatedPerson,
				city: address.city,
				email: values.email,
				firstName: values.firstName,
				lastName: values.lastName,
				phoneNumber: values.phoneNumber,
				postalCode: address.postalCode,
				privacyRequest: values.privacyRequest,
				state: address.state,
				deleteAcknowledgement: values.privacyRequest === 'Privacy Deletion Request' ? values.deleteAcknowledgement : false,
			},
		};

		await submitForm({ variables: { input } });
	};

	const onFormSuccess = () => {
		setValues({ firstName: '', lastName: '', phoneNumber: '', email: '', associatedPerson: false, privacyRequest: '', deleteAcknowledgement: false });
		setAddress({});
		setPreviousAddress(true);
		window.scrollTo({ top: 0, behavior: 'smooth' });
		setStatus('success');
	};

	return (
		<div
			className='ccpa-form mt-6'
			data-testid='ccpa-form'
		>
			{status === 'success' && (
				<Alert
					type='success'
					className='mb-4'
					hideClose
				>
					Form has been successfully submitted!
				</Alert>
			)}

			<form
				onSubmit={onFormSubmit}
				noValidate
			>
				<Row>
					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='firstName'
							labelText='First Name'
							onChange={onInputChange}
							error={errors.firstName ? true : false}
							errorText={errors.firstName}
							autoComplete='given-name'
							value={values.firstName}
							required
						/>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='lastName'
							labelText='Last Name'
							onChange={onInputChange}
							error={errors.lastName ? true : false}
							errorText={errors.lastName}
							autoComplete='family-name'
							value={values.lastName}
							required
						/>
					</Column>

					<Column xs={12}>
						<AddressValidation
							className='ccpa-address'
							addressComplete={[addressComplete, setAddressComplete]}
							addressInputs={{
								line1: {
									label: 'Address',
									xs: 12,
								},
								city: { xs: 12, md: 6 },
								state: { label: 'State/Province', xs: 12, md: 6 },
								postalCode: { xs: 12, md: 6 },
							}}
							addressInput={{ line_1: '', city: '', state: '', postcode: '' }}
							addressOutput={[address, setAddress]}
							addressModified={[addressModified, setAddressModified]}
							addressValidate={[forceAddressValidation, setForceAddressValidation]}
							usePreviousAddress={[previousAddress, setPreviousAddress]}
							allowInternational
						/>
					</Column>

					<Column xs={12} />

					<Column
						xs={12}
						md={6}
					>
						<TextInput
							type='tel'
							id='phoneNumber'
							labelText='Phone Number'
							format='(###) ###-####'
							onChange={onInputChange}
							error={errors.phoneNumber ? true : false}
							errorText={errors.phoneNumber}
							autoComplete='tel'
							value={values.phoneNumber}
							required
						/>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<TextInput
							type='email'
							id='email'
							labelText='Email Address'
							onChange={onInputChange}
							onBlur={onInputBlur}
							error={errors.email ? true : false}
							errorText={errors.email}
							autoComplete='email'
							value={values.email}
							required
						/>
					</Column>

					<Column xs={12}>
						<div>
							<Label className='text-required'>Associated Person</Label>
						</div>

						<Checkbox
							id='associatedPerson'
							labelText='By checking this box, you are acknowledging that you are the person associated to the customer information entered on this screen.'
							onChange={onInputChange}
							error={errors.associatedPerson ? true : false}
							errorText={errors.associatedPerson}
							groupClass='[&>label]:items-start [&_span]:mt-1'
							checked={values.associatedPerson}
						/>
					</Column>

					<Column xs={12}>
						<div className='my-4 border border-neutral-200 bg-neutral-50 px-6 pt-4 [&>legend]:text-body-2'>
							<p className='text-required text-body-2 mb-2 font-semibold text-black'>Please indicate whether you want your information sent to you or deleted</p>
							<List
								bullets='none'
								layout='inline'
								className='gap-x-10'
							>
								<li>
									<Checkbox
										type='radio'
										id='dataRequest'
										group='privacyRequest'
										labelText='Privacy Data Request'
										value='Privacy Data Request'
										onChange={onInputChange}
										checked={values.privacyRequest === 'Privacy Data Request'}
									/>
								</li>
								<li>
									<Checkbox
										type='radio'
										id='deleteRequest'
										group='privacyRequest'
										labelText='Privacy Deletion Request'
										value='Privacy Deletion Request'
										onChange={onInputChange}
										checked={values.privacyRequest === 'Privacy Deletion Request'}
									/>
								</li>
							</List>

							{errors.privacyRequest && (
								<Alert
									type='error'
									className='mb-4'
									hideClose
								>
									{errors.privacyRequest}
								</Alert>
							)}
						</div>
					</Column>

					{values.privacyRequest === 'Privacy Deletion Request' && (
						<Column xs={12}>
							<div>
								<Label className='text-required'>Delete Acknowledgement</Label>
							</div>

							<Checkbox
								id='deleteAcknowledgement'
								labelText='I acknowledge this will delete my account, information, and access to all of Pella Corporations services.'
								onChange={onInputChange}
								error={errors.deleteAcknowledgement ? true : false}
								errorText={errors.deleteAcknowledgement}
								groupClass='[&>label]:items-start [&_span]:mt-1'
							/>
						</Column>
					)}

					<Column
						xs={12}
						className='mt-4'
					>
						<Button
							color='primary-alt'
							type='submit'
							disabled={status === 'processing'}
						>
							{status === 'processing' ? 'Processing...' : 'Submit Request'}
						</Button>
					</Column>

					<Column
						xs={12}
						className='mt-5'
					>
						<p>Your privacy is important to us. The information you submit here will be retained and used only as is necessary to respond to your request and to keep a record of the request.</p>
					</Column>
				</Row>
			</form>
		</div>
	);
};

export default CCPAForm;
